"use client";

import { notifications } from "@mantine/notifications";
import { SubClassActions } from "Components/Admin/AdminProduct/AdminSubClass/SubClassActions";
import { SubClassForm } from "Components/Admin/AdminProduct/AdminSubClass/SubClassForm";
import { SubClassTable } from "Components/Admin/AdminProduct/AdminSubClass/SubClassTable";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useEffect, useState } from "react";
import {
  useBulkDeleteSubClassesMutation,
  useCreateSubClassMutation,
  useDeleteSubClassMutation,
  useGetAdminSubClassesListQuery,
  useUpdateSubClassMutation,
} from "Services/Admin/SubClassApiSlice";
import {
  SubClassAdminRequestDTO,
  SubClassMstDTO,
} from "Types/Admin/AdminSubClassType";

const PLANT = process.env.REACT_APP_PLANT;
const DEFAULT_PAGE_SIZE = 100;

const AdminSubClass = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error: subClassesError,
    refetch,
  } = useGetAdminSubClassesListQuery({
    page,
    size: pageSize,
    search: debouncedSearch || undefined,
  });

  useEffect(() => {
    if (subClassesError) {
      console.error("Failed to load sub categories:", subClassesError);
    }
  }, [subClassesError]);

  const subClasses: SubClassMstDTO[] = data?.subClasses || [];
  const totalSubClasses = data?.totalSubClasses || 0;

  const [createSubClass, { isLoading: isCreating }] =
    useCreateSubClassMutation();

  const [updateSubClass, { isLoading: isUpdating }] =
    useUpdateSubClassMutation();

  const [deleteSubClass] = useDeleteSubClassMutation();

  const [bulkDeleteSubClasses] = useBulkDeleteSubClassesMutation();
  const [selectedSubClasses, setSelectedSubClasses] = useState<number[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingSubClass, setEditingSubClass] = useState<SubClassMstDTO | null>(
    null,
  );
  const [selectedSubClassId, setSelectedSubClassId] = useState<string | null>(
    null,
  );

  const subClassMappingFromMstToRequest = (
    subClassMst: SubClassMstDTO | null,
  ) => {
    if (subClassMst != null) {
      return {
        subClassCode: subClassMst.subClassCode,
        subClassName: subClassMst.subClassName,
        categoryCode: subClassMst.categoryCode,
        isActive: subClassMst.isActive,
      } as SubClassAdminRequestDTO;
    } else {
      return null;
    }
  };

  const handleViewSubClass = async (id: string) => {
    try {
      const subClassToView = subClasses.find(
        (subClass) => subClass.subClassCode === id,
      );
      if (subClassToView) {
        setEditingSubClass(subClassToView);
        setViewMode(true);
        setShowForm(true);
      }
    } catch (error: any) {
      console.error("Failed to get Sub Category details:", error);
    }
  };

  const handleEditSubClass = async (id: string) => {
    try {
      const subClassToEdit = subClasses.find(
        (subClass) => subClass.subClassCode === id,
      );
      if (subClassToEdit) {
        setEditingSubClass(subClassToEdit);
        setViewMode(false);
        setShowForm(true);
      }
    } catch (error: any) {
      console.error("Failed to get Sub Category details:", error);
    }
  };

  const isPending = isCreating || isUpdating;

  const handleSubmit = async (data: SubClassAdminRequestDTO) => {
    if (isPending) return;
    try {
      if (editingSubClass) {
        const updatedData = {

          subClassName: data.subClassName,
          categoryCode: data.categoryCode,
          isActive: data.isActive,
          plant: PLANT!,
        };

        const res = await updateSubClass({
          id: editingSubClass.subClassCode,
          data: updatedData,
        }).unwrap();

        notifications.show({
          message: res.message,
          color: "teal",
        });
      } else {
        const { subClassCode, ...createPayload } = data;

        const createData = {
          ...createPayload,
          plant: PLANT!,
        };

        const res = await createSubClass(createData).unwrap();

        notifications.show({
          message: res.message,
          color: "teal",
        });
      }

      await refetch();
      setShowForm(false);
      setEditingSubClass(null);
    } catch (error: any) {
      let errorMessage = "We couldn't save the sub category. Please try again.";

      if (error.data?.results?.message) {
        errorMessage = error.data.results.message;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      notifications.show({
        message: errorMessage,
        color: "red",
      });
      console.error("Failed to save Sub Category:", error);
    }
  };

  const handleDeleteSubClass = async (id: string) => {
    try {
      const res = await deleteSubClass(id).unwrap();
      await refetch();
      notifications.show({
        message: res.message,
        color: "teal",
      });
    } catch (error: any) {
      let errorMessage = "Failed to delete Sub Category";

      if (error.data?.results?.message) {
        errorMessage = error.data.results.message;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      notifications.show({
        message: errorMessage,
        color: "red",
      });
      console.error("Failed to delete Sub Category:", error);
    }
  };

  const handleTableSelect = (ids: number[]) => {
    setSelectedSubClasses(ids);
    setSelectedSubClassId(
      ids.length > 0
        ? subClasses.find((s) => s.id === ids[0])?.subClassCode || null
        : null,
    );
  };

  const handleBulkDelete = async () => {
    if (selectedSubClasses.length === 0) return;

    try {
      const count = selectedSubClasses.length;
      const res = await bulkDeleteSubClasses({ ids: selectedSubClasses }).unwrap();

      notifications.show({
        message: res.message,
        color: "teal",
      });

      setSelectedSubClasses([]);
      setSelectedSubClassId(null);
      refetch();
    } catch (error: any) {
      let errorMessage = "Failed to delete sub categories";

      if (error.data?.results?.message) {
        errorMessage = error.data.results.message;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      notifications.show({
        message: errorMessage,
        color: "red",
      });
      console.error("Failed to bulk delete sub categories:", error);
    }
  };

  if (isLoading && !showForm) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <Header
            title="Sub Category"
            onAdd={() => setShowForm(true)}
            addLabel="Add Sub Category"
          />
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden p-4 pt-8">
            <SubClassActions
              onSearchChange={setSearchText}
              isDeleteDisabled={selectedSubClasses.length === 0}
              onDelete={handleBulkDelete}
            />
            <SubClassTable
              subClasses={subClasses}
              onSelectSubClass={handleTableSelect}
              onDeleteSubClass={handleDeleteSubClass}
              onViewSubClass={handleViewSubClass}
              onEditSubClass={handleEditSubClass}
              selectedSubClass={selectedSubClassId}
              isLoading={isFetching}
              isError={isError}
              page={page}
              pageSize={pageSize}
              totalItems={totalSubClasses}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingSubClass(null);
              setViewMode(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left-icon lucide-arrow-left h-4 w-4"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <Header
            title={
              viewMode
                ? "View Sub Category"
                : editingSubClass
                  ? "Edit Sub Category"
                  : "Add Sub Category"
            }
          />
          <SubClassForm
            initialValues={
              subClassMappingFromMstToRequest(editingSubClass) || {
                subClassCode: "",
                subClassName: "",
                categoryCode: "",
                isActive: "Y",
              }
            }
            onSubmit={handleSubmit}
            isSubmitting={isPending}
            onCancel={() => {
              setShowForm(false);
              setViewMode(false);
              setEditingSubClass(null);
            }}
            mode={viewMode ? "view" : editingSubClass ? "edit" : "add"}
          />
        </>
      )}
    </div>
  );
};

export default AdminSubClass;
